import { Link } from "react-router-dom";
import { CreditCard, Users, Zap, ClipboardList, Package, ShieldCheck, Wallet, ArrowRight } from "lucide-react";
import { money } from "@/lib/api";
import { DemoNotice, ErrorBox, PageHead, Spinner, useFetch } from "@/components/common";

const sum = (o = {}) => Object.values(o).reduce((a, b) => a + b, 0);

const Stat = ({ icon: Icon, label, value, sub, to, testId }) => (
  <Link to={to} className="surface group flex flex-col gap-3 p-5 transition-shadow hover:shadow-md" data-testid={testId}>
    <div className="flex items-center justify-between">
      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-purple-50 to-blue-50 text-purple-700"><Icon size={19} /></span>
      <ArrowRight size={15} className="text-slate-300 transition-transform group-hover:translate-x-1 group-hover:text-purple-600" />
    </div>
    <div>
      <p className="text-3xl font-extrabold tracking-tight text-slate-900">{value}</p>
      <p className="text-xs font-semibold text-slate-500">{label}</p>
      {sub && <p className="mt-1 text-[11px] text-slate-400">{sub}</p>}
    </div>
  </Link>
);

export default function Overview() {
  const { data: r, error, loading } = useFetch("/admin/reports");
  if (loading) return <Spinner />;
  if (!r) return <ErrorBox error={error} />;
  const revenue = r.sales.CASH.total + r.sales.QRPH.total;
  return (
    <div className="space-y-6">
      <PageHead eyebrow="Admin" title="Overview" sub="GIA CARE+ Phase 1 operations at a glance" />
      <DemoNotice>Cards are activated only by a cashier after confirmed payment. QR Ph activates only after PayMongo server-side verification. Insurance activation and prescription issuance remain disabled.</DemoNotice>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat icon={Package} label="Packages" value={sum(r.packages)} sub={`${r.packages.ACTIVE || 0} active`} to="/admin/packages" testId="stat-packages" />
        <Stat icon={CreditCard} label="Total physical cards" value={sum(r.cards)} sub={`${r.cards.AVAILABLE || 0} available`} to="/admin/cards" testId="stat-cards" />
        <Stat icon={Wallet} label="Pending payments" value={r.pending_payments} sub={`${r.cards.PAYMENT_PENDING || 0} cards locked`} to="/admin/payments" testId="stat-pending" />
        <Stat icon={Zap} label="Activated cards" value={r.cards.ACTIVATED || 0} sub="awaiting customer registration" to="/admin/activations" testId="stat-activated" />
        <Stat icon={Users} label="Registered cards" value={r.cards.REGISTERED || 0} sub={`${r.customers} customer accounts`} to="/admin/customers" testId="stat-registered" />
        <Stat icon={Wallet} label="Cash sales" value={r.sales.CASH.count} sub={money(r.sales.CASH.total)} to="/admin/payments" testId="stat-cash" />
        <Stat icon={Wallet} label="QR Ph sales" value={r.sales.QRPH.count} sub={money(r.sales.QRPH.total)} to="/admin/payments" testId="stat-qrph" />
        <Stat icon={Wallet} label="Total collected revenue" value={money(revenue)} to="/admin/payments" testId="stat-revenue" />
        <Stat icon={ClipboardList} label="Open service requests" value={(r.service_requests.REQUESTED || 0) + (r.service_requests.CONFIRMED || 0)} sub={`${r.service_requests.COMPLETED || 0} completed`} to="/admin/requests" testId="stat-requests" />
        <Stat icon={ShieldCheck} label="Insurance pending" value={r.insurance.PENDING || 0} sub={`${r.insurance.CONFIRMED || 0} confirmed`} to="/admin/insurance" testId="stat-insurance" />
      </div>
    </div>
  );
}
